import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { message, Button, Input, Spin, Empty } from "antd";
import { CheckCircle2, XCircle, FileText, Download } from "lucide-react";
import { IQuote } from "../../../types/api/quote";
import axios from "../../../lib/axios";
import dayjs from "dayjs";

export default function QuotePublicPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<IQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [responding, setResponding] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`/public-quotes/${token}`)
      .then((res) => {
        setQuote(res.data.data);
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Quote not found or link expired.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleRespond = (action: "accept" | "reject") => {
    if (!token) return;
    setResponding(true);
    axios
      .post(`/public-quotes/${token}/respond`, { action, notes })
      .then((res) => {
        message.success(res.data.message || `Quote ${action}ed successfully.`);
        setQuote(res.data.data);
      })
      .catch((err) => {
        message.error(err.response?.data?.message || `Failed to ${action} quote.`);
      })
      .finally(() => setResponding(false));
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50">
        <Spin size="large" tip="Loading your quote..." />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm text-center">
          <XCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Unavailable</h2>
          <p className="text-slate-500">{error || "This quote could not be loaded."}</p>
        </div>
      </div>
    );
  }

  const fmt = (val: number | string | undefined) =>
    Number(val ?? 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" });

  const isResponded = ["accepted", "rejected", "converted", "expired"].includes(quote.status);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* HEADER */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="text-sm text-blue-600 font-semibold mb-1">Quote {quote.quote_number}</div>
            <h1 className="text-2xl font-bold text-slate-800">{quote.title}</h1>
            <div className="text-sm text-slate-500 mt-1">
              Issued: {dayjs(quote.issue_date).format("DD MMM YYYY")} 
              {quote.expiry_date && <span className="ml-2 px-2 border-l border-slate-200">Valid until: {dayjs(quote.expiry_date).format("DD MMM YYYY")}</span>}
            </div>
          </div>
          {quote.pdf_url && (
            <Button 
              type="primary" 
              icon={<Download size={16} />}
              onClick={() => window.open(quote.pdf_url!, "_blank")}
              className="bg-slate-900 shadow-md h-10"
            >
              Download PDF
            </Button>
          )}
        </div>

        {/* STATUS BANNER */}
        {isResponded && (
          <div className={`p-4 rounded-xl border flex items-center gap-3 ${
            quote.status === "accepted" || quote.status === "converted"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : quote.status === "rejected"
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}>
            {quote.status === "accepted" || quote.status === "converted" ? (
              <CheckCircle2 size={24} className="text-emerald-500" />
            ) : quote.status === "rejected" ? (
              <XCircle size={24} className="text-red-500" />
            ) : (
              <FileText size={24} className="text-amber-500" />
            )}
            <div>
              <div className="font-semibold capitalize">Quote {quote.status}</div>
              <div className="text-sm opacity-90">This quote was responded to on {dayjs(quote.responded_at || quote.updated_at).format("DD MMM YYYY")}.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* MAIN CONTENT */}
          <div className="lg:col-span-2 space-y-6">
            
            {quote.description && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Description</h2>
                <div className="text-slate-600 whitespace-pre-wrap text-sm leading-relaxed">{quote.description}</div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-6">Line Items</h2>
              {quote.line_items && quote.line_items.length > 0 ? (
                <div className="space-y-4">
                  {quote.line_items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                      <div>
                        <div className="font-medium text-slate-800">{item.description}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {item.quantity} × {fmt(item.unit_price)} (VAT: {item.vat_rate}%)
                        </div>
                      </div>
                      <div className="font-semibold text-slate-800">{fmt(item.line_total)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No line items provided." />
              )}

              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="space-y-3 max-w-xs ml-auto">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Subtotal</span>
                    <span>{fmt(quote.subtotal)}</span>
                  </div>
                  {Number(quote.discount_amount) > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Discount</span>
                      <span>-{fmt(quote.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>VAT</span>
                    <span>{fmt(quote.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-3 border-t border-slate-100">
                    <span>Total</span>
                    <span>{fmt(quote.total)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {(quote.notes || quote.terms) && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
                {quote.notes && (
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-2">Additional Notes</h2>
                    <div className="text-slate-600 text-sm whitespace-pre-wrap">{quote.notes}</div>
                  </div>
                )}
                {quote.terms && (
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-2">Terms & Conditions</h2>
                    <div className="text-slate-500 text-xs whitespace-pre-wrap">{quote.terms}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ACTION PANEL */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Your Response</h2>
              
              {isResponded ? (
                <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <p>You have already responded to this quote.</p>
                  {quote.client_response_notes && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <span className="block text-xs font-semibold text-slate-700 mb-1">Your notes:</span>
                      <span className="italic">"{quote.client_response_notes}"</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">Add a note (optional)</label>
                    <Input.TextArea 
                      rows={3} 
                      placeholder="Any questions or comments?"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                  <Button 
                    type="primary" 
                    size="large"
                    className="w-full bg-emerald-600 hover:bg-emerald-500 border-none shadow-md"
                    loading={responding}
                    onClick={() => handleRespond("accept")}
                  >
                    Accept Quote
                  </Button>
                  <Button 
                    size="large"
                    danger
                    className="w-full"
                    disabled={responding}
                    onClick={() => handleRespond("reject")}
                  >
                    Decline Quote
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
